import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { bazaarCategoryAPI, bazaarListingAPI } from '../lib/api';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { ImageUpload } from '../components/ui/image-upload';
import { toast } from 'sonner';
import { 
  ArrowLeft, 
  ShoppingBag,
  Tag,
  Plus,
  Package,
  Phone,
  User,
  Calendar,
  Search,
  Gift,
  DollarSign,
  Trash2,
  CheckCircle
} from 'lucide-react';

const CONDITION_OPTIONS = [
  { value: 'new', label: 'New' },
  { value: 'like_new', label: 'Like New' },
  { value: 'good', label: 'Good' },
  { value: 'fair', label: 'Fair' }
];

const PLACEHOLDER_IMAGE = "https://images.unsplash.com/photo-1560393464-5c69a73c5770?w=400&h=300&fit=crop";

export default function RuchiBoxBazaar() {
  const navigate = useNavigate();
  const { user, isAuthenticated } = useAuth();
  
  const [categories, setCategories] = useState([]);
  const [listings, setListings] = useState([]);
  const [myListings, setMyListings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('browse');
  
  // Filters
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [selectedType, setSelectedType] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  
  // Create Listing Modal
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [createLoading, setCreateLoading] = useState(false);
  const [listingForm, setListingForm] = useState({
    category_id: '',
    title: '',
    description: '',
    listing_type: 'giveaway',
    price: '',
    condition: 'good',
    image_url: ''
  });

  useEffect(() => {
    loadCategories();
    loadListings();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (isAuthenticated && user?.id) {
      loadMyListings();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated, user?.id]);

  const loadCategories = async () => {
    try {
      const response = await bazaarCategoryAPI.getAll();
      setCategories(response.data);
    } catch (error) {
      console.error('Failed to load categories');
    }
  };

  const loadListings = async () => {
    setLoading(true);
    try {
      const params = { status: 'active' };
      if (user?.entity_id) {
        params.entity_id = user.entity_id;
      }
      const response = await bazaarListingAPI.getAll(params);
      setListings(response.data);
    } catch (error) {
      console.error('Failed to load listings');
    } finally {
      setLoading(false);
    }
  };

  const loadMyListings = async () => {
    if (!user?.id) return;
    try {
      const response = await bazaarListingAPI.getMyListings(user.id);
      setMyListings(response.data);
    } catch (error) {
      console.error('Failed to load my listings');
    }
  };

  const handleCreateListing = async () => {
    if (!listingForm.category_id || !listingForm.title || !listingForm.description) {
      toast.error('Please fill in all required fields');
      return;
    }
    
    if (listingForm.listing_type === 'for_sale' && (!listingForm.price || parseFloat(listingForm.price) <= 0)) {
      toast.error('Please enter a valid price');
      return;
    }
    
    setCreateLoading(true);
    try {
      const data = {
        ...listingForm,
        price: listingForm.listing_type === 'for_sale' ? parseFloat(listingForm.price) : null
      };
      
      await bazaarListingAPI.create(data, user.id);
      toast.success('Item listed successfully!');
      setShowCreateModal(false);
      resetForm();
      loadListings();
      loadMyListings();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create listing');
    } finally {
      setCreateLoading(false);
    }
  };

  const handleUpdateStatus = async (listingId, status) => {
    try {
      await bazaarListingAPI.updateStatus(listingId, status, user.id);
      toast.success(`Item marked as ${status === 'sold' ? 'sold' : 'donated'}!`);
      loadListings();
      loadMyListings();
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  const handleDeleteListing = async (listingId) => {
    if (!window.confirm('Are you sure you want to remove this listing?')) return;
    
    try {
      await bazaarListingAPI.delete(listingId, user.id);
      toast.success('Listing removed');
      loadListings();
      loadMyListings();
    } catch (error) {
      toast.error('Failed to remove listing');
    }
  };

  const resetForm = () => {
    setListingForm({
      category_id: '',
      title: '',
      description: '',
      listing_type: 'giveaway',
      price: '',
      condition: 'good',
      image_url: ''
    });
  };

  // Filter listings
  const filteredListings = listings.filter(listing => {
    const matchesCategory = selectedCategory === 'all' || listing.category_id === selectedCategory;
    const matchesType = selectedType === 'all' || listing.listing_type === selectedType;
    const matchesSearch = !searchQuery || 
      listing.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      listing.description.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesCategory && matchesType && matchesSearch;
  });

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('en-IN', {
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    });
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-rose-50 to-white">
      {/* Header */}
      <header className="glass-header sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <Button
              variant="ghost"
              onClick={() => navigate('/')}
              className="pl-0"
              data-testid="back-btn"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Home
            </Button>
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-full bg-rose-500 flex items-center justify-center">
                <ShoppingBag className="w-5 h-5 text-white" />
              </div>
              <span className="font-heading text-xl font-bold">RuchiBox Bazaar</span>
            </div>
            {isAuthenticated && (
              <Button 
                onClick={() => setShowCreateModal(true)}
                className="bg-rose-500 hover:bg-rose-600 rounded-full"
                data-testid="create-listing-btn"
              >
                <Plus className="w-4 h-4 mr-2" />
                List Item
              </Button>
            )}
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 py-8">
        {/* Hero Section */}
        <div className="text-center mb-8">
          <div className="w-20 h-20 rounded-full bg-gradient-to-br from-rose-500 to-pink-500 flex items-center justify-center mx-auto mb-4 shadow-lg">
            <ShoppingBag className="w-10 h-10 text-white" />
          </div>
          <h1 className="font-heading text-3xl font-bold text-foreground mb-2">
            Community Marketplace
          </h1>
          <p className="text-muted-foreground max-w-xl mx-auto">
            Buy, sell, or give away items within your community. Reduce waste and strengthen neighborhood bonds!
          </p>
        </div>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="mb-6">
          <TabsList className="grid w-full max-w-md mx-auto grid-cols-2">
            <TabsTrigger value="browse" data-testid="tab-browse">
              <Search className="w-4 h-4 mr-2" />
              Browse Items
            </TabsTrigger>
            <TabsTrigger value="my-listings" data-testid="tab-my-listings" disabled={!isAuthenticated}>
              <Package className="w-4 h-4 mr-2" />
              My Listings
            </TabsTrigger>
          </TabsList>

          {/* Browse Tab */}
          <TabsContent value="browse" className="mt-6">
            {/* Filters */}
            <div className="flex flex-wrap gap-3 mb-6">
              <div className="flex-1 min-w-[200px]">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input
                    placeholder="Search items..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="pl-10"
                    data-testid="search-input"
                  />
                </div>
              </div>
              <Select value={selectedCategory} onValueChange={setSelectedCategory}>
                <SelectTrigger className="w-[180px]" data-testid="category-filter">
                  <SelectValue placeholder="Category" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Categories</SelectItem>
                  {categories.map(cat => (
                    <SelectItem key={cat.id} value={cat.id}>{cat.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Select value={selectedType} onValueChange={setSelectedType}>
                <SelectTrigger className="w-[150px]" data-testid="type-filter">
                  <SelectValue placeholder="Type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Types</SelectItem>
                  <SelectItem value="for_sale">For Sale</SelectItem>
                  <SelectItem value="giveaway">Giveaway</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Listings Grid */}
            {loading ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {[1, 2, 3, 4, 5, 6].map(i => (
                  <Card key={i} className="animate-pulse">
                    <div className="h-48 bg-gray-200" />
                    <CardContent className="p-4 space-y-2">
                      <div className="h-4 bg-gray-200 rounded w-3/4" />
                      <div className="h-3 bg-gray-200 rounded w-1/2" />
                    </CardContent>
                  </Card>
                ))}
              </div>
            ) : filteredListings.length === 0 ? (
              <Card className="border-2 border-dashed border-rose-200 bg-rose-50/50">
                <CardContent className="p-12 text-center">
                  <Package className="w-16 h-16 text-rose-300 mx-auto mb-4" />
                  <h3 className="font-heading text-xl font-bold text-foreground mb-2">
                    No items found
                  </h3>
                  <p className="text-muted-foreground mb-4">
                    {searchQuery || selectedCategory !== 'all' || selectedType !== 'all'
                      ? 'Try adjusting your filters'
                      : 'Be the first to list an item in the Bazaar!'}
                  </p>
                  {isAuthenticated && (
                    <Button 
                      onClick={() => setShowCreateModal(true)}
                      className="bg-rose-500 hover:bg-rose-600 rounded-full"
                    >
                      <Plus className="w-4 h-4 mr-2" />
                      List Your First Item
                    </Button>
                  )}
                </CardContent>
              </Card>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {filteredListings.map(listing => (
                  <Card key={listing.id} className="overflow-hidden card-hover" data-testid={`listing-${listing.id}`}>
                    <div className="relative h-48">
                      <img
                        src={listing.image_url || PLACEHOLDER_IMAGE}
                        alt={listing.title}
                        className="w-full h-full object-cover"
                        onError={(e) => { e.target.src = PLACEHOLDER_IMAGE; }}
                      />
                      <Badge 
                        className={`absolute top-2 right-2 ${
                          listing.listing_type === 'giveaway' 
                            ? 'bg-green-500 hover:bg-green-600' 
                            : 'bg-blue-500 hover:bg-blue-600'
                        }`}
                      >
                        {listing.listing_type === 'giveaway' ? (
                          <><Gift className="w-3 h-3 mr-1" /> Free</>
                        ) : (
                          <><DollarSign className="w-3 h-3 mr-1" /> ₹{listing.price}</>
                        )}
                      </Badge>
                    </div>
                    <CardContent className="p-4">
                      <div className="flex items-start justify-between mb-2">
                        <h3 className="font-semibold text-foreground line-clamp-1">{listing.title}</h3>
                      </div>
                      <p className="text-sm text-muted-foreground line-clamp-2 mb-3">{listing.description}</p>
                      <div className="flex flex-wrap gap-2 mb-3">
                        <Badge variant="outline" className="text-xs">
                          <Tag className="w-3 h-3 mr-1" />
                          {listing.category_name}
                        </Badge>
                        {listing.condition && (
                          <Badge variant="outline" className="text-xs capitalize">
                            {listing.condition.replace('_', ' ')}
                          </Badge>
                        )}
                      </div>
                      <div className="flex items-center justify-between text-xs text-muted-foreground">
                        <span className="flex items-center gap-1">
                          <User className="w-3 h-3" />
                          {listing.seller_name}
                        </span>
                        <span className="flex items-center gap-1">
                          <Calendar className="w-3 h-3" />
                          {formatDate(listing.created_at)}
                        </span>
                      </div>
                      {listing.seller_phone && (
                        <Button 
                          variant="outline" 
                          size="sm" 
                          className="w-full mt-3"
                          onClick={() => window.open(`tel:${listing.seller_phone}`)}
                        >
                          <Phone className="w-4 h-4 mr-2" />
                          Contact Seller
                        </Button>
                      )}
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>

          {/* My Listings Tab */}
          <TabsContent value="my-listings" className="mt-6">
            {!isAuthenticated ? (
              <Card className="border-2 border-dashed border-rose-200 bg-rose-50/50">
                <CardContent className="p-12 text-center">
                  <User className="w-16 h-16 text-rose-300 mx-auto mb-4" />
                  <h3 className="font-heading text-xl font-bold text-foreground mb-2">
                    Login Required
                  </h3>
                  <p className="text-muted-foreground mb-4">
                    Please login to view and manage your listings.
                  </p>
                  <Button onClick={() => navigate('/auth')} className="bg-rose-500 hover:bg-rose-600 rounded-full">
                    Login
                  </Button>
                </CardContent>
              </Card>
            ) : myListings.length === 0 ? (
              <Card className="border-2 border-dashed border-rose-200 bg-rose-50/50">
                <CardContent className="p-12 text-center">
                  <Package className="w-16 h-16 text-rose-300 mx-auto mb-4" />
                  <h3 className="font-heading text-xl font-bold text-foreground mb-2">
                    No listings yet
                  </h3>
                  <p className="text-muted-foreground mb-4">
                    Start selling or giving away items you no longer need!
                  </p>
                  <Button 
                    onClick={() => setShowCreateModal(true)}
                    className="bg-rose-500 hover:bg-rose-600 rounded-full"
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    Create Your First Listing
                  </Button>
                </CardContent>
              </Card>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {myListings.map(listing => (
                  <Card key={listing.id} className="overflow-hidden" data-testid={`my-listing-${listing.id}`}>
                    <div className="relative h-40">
                      <img
                        src={listing.image_url || PLACEHOLDER_IMAGE}
                        alt={listing.title}
                        className="w-full h-full object-cover"
                        onError={(e) => { e.target.src = PLACEHOLDER_IMAGE; }}
                      />
                      <Badge 
                        className={`absolute top-2 right-2 ${
                          listing.status === 'active' ? 'bg-green-500' :
                          listing.status === 'sold' ? 'bg-blue-500' :
                          listing.status === 'donated' ? 'bg-purple-500' : 'bg-gray-500'
                        }`}
                      >
                        {listing.status.charAt(0).toUpperCase() + listing.status.slice(1)}
                      </Badge>
                    </div>
                    <CardContent className="p-4">
                      <h3 className="font-semibold text-foreground mb-1 line-clamp-1">{listing.title}</h3>
                      <p className="text-sm text-muted-foreground mb-3">
                        {listing.listing_type === 'giveaway' ? 'Giveaway' : `₹${listing.price}`}
                      </p>
                      {listing.status === 'active' && (
                        <div className="flex gap-2">
                          <Button
                            size="sm"
                            variant="outline"
                            className="flex-1 text-green-600 border-green-600 hover:bg-green-50"
                            onClick={() => handleUpdateStatus(listing.id, listing.listing_type === 'giveaway' ? 'donated' : 'sold')}
                          >
                            <CheckCircle className="w-3 h-3 mr-1" />
                            {listing.listing_type === 'giveaway' ? 'Donated' : 'Sold'}
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            className="text-red-600 border-red-600 hover:bg-red-50"
                            onClick={() => handleDeleteListing(listing.id)}
                          >
                            <Trash2 className="w-3 h-3" />
                          </Button>
                        </div>
                      )}
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>
        </Tabs>
      </main>

      {/* Create Listing Modal */}
      <Dialog open={showCreateModal} onOpenChange={setShowCreateModal}>
        <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ShoppingBag className="w-5 h-5 text-rose-500" />
              List an Item
            </DialogTitle>
            <DialogDescription>
              Share something with your community - sell it or give it away!
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 mt-2">
            <div>
              <Label htmlFor="listing-type">Listing Type *</Label>
              <Select 
                value={listingForm.listing_type}
                onValueChange={(value) => setListingForm(prev => ({ ...prev, listing_type: value }))}
              >
                <SelectTrigger data-testid="listing-type-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="giveaway">
                    <span className="flex items-center gap-2">
                      <Gift className="w-4 h-4 text-green-500" />
                      Giveaway (Free)
                    </span>
                  </SelectItem>
                  <SelectItem value="for_sale">
                    <span className="flex items-center gap-2">
                      <DollarSign className="w-4 h-4 text-blue-500" />
                      For Sale
                    </span>
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div>
              <Label htmlFor="category">Category *</Label>
              <Select 
                value={listingForm.category_id}
                onValueChange={(value) => setListingForm(prev => ({ ...prev, category_id: value }))}
              >
                <SelectTrigger data-testid="category-select">
                  <SelectValue placeholder="Select a category" />
                </SelectTrigger>
                <SelectContent>
                  {categories.map(cat => (
                    <SelectItem key={cat.id} value={cat.id}>{cat.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div>
              <Label htmlFor="title">Title *</Label>
              <Input
                id="title"
                placeholder="e.g., Wooden Study Table, Books Collection..."
                value={listingForm.title}
                onChange={(e) => setListingForm(prev => ({ ...prev, title: e.target.value }))}
                data-testid="title-input"
              />
            </div>
            
            <div>
              <Label htmlFor="description">Description *</Label>
              <Textarea
                id="description"
                placeholder="Describe your item, its condition, and any other details..."
                value={listingForm.description}
                onChange={(e) => setListingForm(prev => ({ ...prev, description: e.target.value }))}
                rows={3}
                data-testid="description-input"
              />
            </div>
            
            {listingForm.listing_type === 'for_sale' && (
              <div>
                <Label htmlFor="price">Price (₹) *</Label>
                <Input
                  id="price"
                  type="number"
                  placeholder="Enter price"
                  value={listingForm.price}
                  onChange={(e) => setListingForm(prev => ({ ...prev, price: e.target.value }))}
                  data-testid="price-input"
                />
              </div>
            )}
            
            <div>
              <Label htmlFor="condition">Condition</Label>
              <Select 
                value={listingForm.condition}
                onValueChange={(value) => setListingForm(prev => ({ ...prev, condition: value }))}
              >
                <SelectTrigger data-testid="condition-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {CONDITION_OPTIONS.map(opt => (
                    <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div>
              <Label>Item Image</Label>
              <ImageUpload
                value={listingForm.image_url}
                onChange={(url) => setListingForm(prev => ({ ...prev, image_url: url }))}
                placeholder="Upload item photo"
                maxSizeMB={5}
              />
            </div>
            
            <div className="flex gap-3 pt-2">
              <Button
                variant="outline"
                className="flex-1"
                onClick={() => {
                  setShowCreateModal(false);
                  resetForm();
                }}
              >
                Cancel
              </Button>
              <Button
                className="flex-1 bg-rose-500 hover:bg-rose-600"
                onClick={handleCreateListing}
                disabled={createLoading}
                data-testid="submit-listing-btn"
              >
                {createLoading ? (
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
                ) : (
                  <Plus className="w-4 h-4 mr-2" />
                )}
                Create Listing
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
